import React, { useState, useEffect, FormEvent, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PageProps } from "../types/auth";
import { trackPageVisit } from "../utils/pageTracker";

import styles from './styles/CreateGroupPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Curriculum {
  curriculum_id: number;
  name: string;
}

interface Group {
  group_id: number;
  name: string;
  curriculum_id?: number;
}

const CreateGroupPage: React.FC<PageProps> = ({ token, selectedOrgID }) => {
  const [name, setName] = useState<string>("");
  const [curriculums, setCurriculums] = useState<Curriculum[]>([]);
  const [selectedCurriculum, setSelectedCurriculum] = useState<number | null>(null);
  const [error, setError] = useState<string>("");
  const navigate = useNavigate();
  const hasTrackedRef = useRef(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Track page visit
  useEffect(() => {
    if (!hasTrackedRef.current) {
      trackPageVisit("New Group", "/groups/create");
      hasTrackedRef.current = true;
    }
  }, []);

  // Focus the name input when component mounts
  useEffect(() => {
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const fetchCurriculums = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/curriculums`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch curriculums.");
          return;
        }

        const data = await response.json();
        setCurriculums(Array.isArray(data) ? data : []);
      } catch (err) {
        setError("An unexpected error occurred while fetching curriculums.");
      }
    };

    fetchCurriculums();
  }, [token, selectedOrgID]);

  const handleCreateGroup = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Group name cannot be empty.");
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/groups`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
        body: JSON.stringify({
          name,
          curriculum_id: selectedCurriculum || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to create group.");
        return;
      }
      
      const data: Group = await response.json();
      navigate(`/groups/${data.group_id}`);
    } catch (err) {
      setError("An unexpected error occurred while creating the group.");
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>New Group</h1>
      {error && <p className={styles.errorMessage}>{error}</p>}
      
      <form onSubmit={handleCreateGroup} className={styles.form}>
        <div className={styles.formGroup}>
          <label className={styles.label}>Group Name:</label>
          <input
            ref={nameInputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className={styles.input}
          />
        </div>
        
        <div className={styles.formGroup}>
          <label className={styles.label}>Curriculum:</label>
          <select
            value={selectedCurriculum?.toString() || ""}
            onChange={(e) => setSelectedCurriculum(e.target.value ? parseInt(e.target.value, 10) : null)}
            className={styles.select}
          >
            <option value="">No curriculum</option>
            {curriculums.map((curriculum) => (
              <option key={curriculum.curriculum_id} value={curriculum.curriculum_id}>
                {curriculum.name}
              </option>
            ))}
          </select>
        </div>
        
        <div className={styles.buttonContainer}>
          <button type="submit" className={styles.submitButton}>
            Create Group
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateGroupPage; 